import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '../components/AppLayout';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { Plus, Save, Trash2, Pencil, CheckCircle2, XCircle, Clock, Layers } from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';
import { todayISO, currentMonthISO } from '../utils/clinicDate';

const EQUIPMENT_OPTIONS = ['Autoclave','Handpiece','Scalers','Mirrors & Probes','Extraction Forceps','Surgical Kit','Composite Kit','Endodontic Files','Impression Trays','Other'];
const METHOD_OPTIONS    = ['Steam (Autoclave)','Chemical (Glutaraldehyde)','Dry Heat','UV Chamber','Cold Sterilisation','Other'];
const TODAY = () => todayISO();
const NOW   = () => new Date().toTimeString().slice(0,5);

const emptyForm = { date: TODAY(), time: NOW(), equipment: '', method: 'Steam (Autoclave)', cycleNumber: '', itemsCount: 1, items: '', operator: '', batchNumber: '', expiryDate: '', result: 'pass', notes: '' };

const RESULT_STYLE = {
  pass:    { color: '#16a34a', bg: '#f0fdf4', icon: CheckCircle2, label: 'Pass' },
  fail:    { color: '#dc2626', bg: '#fef2f2', icon: XCircle,      label: 'Fail' },
  pending: { color: '#d97706', bg: '#fffbeb', icon: Clock,         label: 'Pending' },
};

function F({ label, children }) {
  return (
    <div className="field">
      <label className="label">{label}</label>
      {children}
    </div>
  );
}

export default function SterilisationPage() {
  const { showToast } = useToast();
  const { user }      = useAuth();
  const isDoctor      = user?.role === 'doctor';

  const [logs, setLogs]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [form, setForm]           = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving]       = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [dateFilter, setDateFilter]     = useState('all');
  const [resultFilter, setResultFilter] = useState('all');
  const [groupBy, setGroupBy]           = useState('none');

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (dateFilter !== 'all')   params.date   = dateFilter;
      if (resultFilter !== 'all') params.result = resultFilter;
      const res = await api.get('/sterilisation', { params });
      setLogs(res.data.logs || []);
    } catch { showToast('Could not load logs.', 'error'); }
    finally { setLoading(false); }
  }, [dateFilter, resultFilter, showToast]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const sf = f => e => setForm(v => ({ ...v, [f]: e.target.value }));

  async function handleSave(e) {
    e.preventDefault();
    if (!form.equipment) { showToast('Equipment name is required.', 'error'); return; }
    setSaving(true);
    try {
      if (editingId) {
        const res = await api.put(`/sterilisation/${editingId}`, form);
        setLogs(prev => prev.map(l => l._id === editingId ? res.data.log : l));
        showToast('Log updated.', 'success');
      } else {
        const res = await api.post('/sterilisation', form);
        setLogs(prev => [res.data.log, ...prev]);
        showToast('Log saved.', 'success');
      }
      setShowForm(false); setEditingId(null); setForm(emptyForm);
    } catch (err) { showToast(err.response?.data?.message || 'Error saving log.', 'error'); }
    finally { setSaving(false); }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await api.delete(`/sterilisation/${deleteTarget._id}`);
      setLogs(prev => prev.filter(l => l._id !== deleteTarget._id));
      showToast('Log deleted.', 'success');
    } catch { showToast('Could not delete.', 'error'); }
    setDeleteTarget(null);
  }

  // Get distinct dates for filter
  const dates = [...new Set(logs.map(l => l.date))].sort().reverse();

  // Grouping
  const getGroupKey = l => {
    if (groupBy === 'date')      return l.date;
    if (groupBy === 'equipment') return l.equipment || 'Unknown';
    if (groupBy === 'result')    return RESULT_STYLE[l.result]?.label || l.result;
    return '__all__';
  };
  const groups = {};
  logs.forEach(l => { const k = getGroupKey(l); if (!groups[k]) groups[k] = []; groups[k].push(l); });

  const passCount = logs.filter(l => l.result === 'pass').length;
  const failCount = logs.filter(l => l.result === 'fail').length;

  return (
    <AppLayout title="Sterilisation Log" subtitle="Track equipment sterilisation cycles and compliance">

      {/* Stats */}
      <div className="stat-grid" style={{ marginBottom: 20 }}>
        {[
          ['Total Records', logs.length, '#2563eb'],
          ['Passed', passCount, '#16a34a'],
          ['Failed', failCount, '#dc2626'],
          ['This Month', logs.filter(l => l.date?.startsWith(currentMonthISO())).length, '#d97706'],
        ].map(([label, val, color]) => (
          <div key={label} className="card stat-card lift3d-card" style={{ padding: '14px 18px', borderTop: `3px solid ${color}` }}>
            <div style={{ fontSize: 11, color: 'var(--color-ink-faint)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{label}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color }}>{val}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="page-toolbar" style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <select className="select" style={{ minWidth: 140 }} value={dateFilter} onChange={e => setDateFilter(e.target.value)}>
          <option value="all">All dates</option>
          {dates.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select className="select" style={{ minWidth: 130 }} value={resultFilter} onChange={e => setResultFilter(e.target.value)}>
          <option value="all">All results</option>
          <option value="pass">Pass ✅</option>
          <option value="fail">Fail ❌</option>
          <option value="pending">Pending ⏳</option>
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Layers size={14} color="var(--color-ink-faint)" />
          <select className="select" style={{ minWidth: 160 }} value={groupBy} onChange={e => setGroupBy(e.target.value)}>
            <option value="none">No grouping</option>
            <option value="date">Group by Date</option>
            <option value="equipment">Group by Equipment</option>
            <option value="result">Group by Result</option>
          </select>
        </div>
        <div style={{ flex: 1 }} />
        {isDoctor && (
          <button className="btn btn-primary" onClick={() => { setShowForm(v => !v); setEditingId(null); setForm(emptyForm); }}>
            <Plus size={15} />{showForm ? 'Cancel' : 'Add Log'}
          </button>
        )}
      </div>

      {/* Add/Edit Form */}
      {showForm && isDoctor && (
        <div className="card" style={{ padding: 20, marginBottom: 20 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 700 }}>
            {editingId ? 'Edit Log' : 'New Sterilisation Log'}
          </h3>
          <form onSubmit={handleSave}>
            <div className="steril-form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 12, marginBottom: 12 }}>
              <F label="Date *"><input className="input" type="date" value={form.date} onChange={sf('date')} /></F>
              <F label="Time"><input className="input" type="time" value={form.time} onChange={sf('time')} /></F>
              <F label="Equipment *">
                <select className="select" value={form.equipment} onChange={sf('equipment')}>
                  <option value="">Select equipment…</option>
                  {EQUIPMENT_OPTIONS.map(e => <option key={e} value={e}>{e}</option>)}
                </select>
              </F>
              <F label="Method">
                <select className="select" value={form.method} onChange={sf('method')}>
                  {METHOD_OPTIONS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </F>
              <F label="Cycle / Run No."><input className="input" value={form.cycleNumber} onChange={sf('cycleNumber')} placeholder="e.g. C-042" /></F>
              <F label="Items Count"><input className="input" type="number" min="0" value={form.itemsCount} onChange={sf('itemsCount')} /></F>
              <F label="Operator / Staff"><input className="input" value={form.operator} onChange={sf('operator')} placeholder="Who performed this?" /></F>
              <F label="Batch / Pouch No."><input className="input" value={form.batchNumber} onChange={sf('batchNumber')} placeholder="Batch number" /></F>
              <F label="Pouch Expiry Date"><input className="input" type="date" value={form.expiryDate} onChange={sf('expiryDate')} /></F>
              <F label="Result">
                <select className="select" value={form.result} onChange={sf('result')}>
                  <option value="pass">✅ Pass</option>
                  <option value="fail">❌ Fail</option>
                  <option value="pending">⏳ Pending</option>
                </select>
              </F>
            </div>
            <F label="Items Sterilised">
              <textarea className="input" rows={2} value={form.items} onChange={sf('items')} placeholder="List items, e.g. extraction forceps ×3, mirrors ×5…" style={{ resize: 'vertical' }} />
            </F>
            <F label="Notes">
              <input className="input" value={form.notes} onChange={sf('notes')} placeholder="Any additional notes…" />
            </F>
            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                <Save size={15} />{saving ? 'Saving…' : editingId ? 'Update' : 'Save Log'}
              </button>
              <button type="button" className="btn btn-outline" onClick={() => { setShowForm(false); setEditingId(null); }}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Log list */}
      {loading ? (
        <div style={{ display: 'grid', gap: 10 }}>{[1,2,3].map(i => <div key={i} className="skeleton" style={{ height: 80, borderRadius: 10 }} />)}</div>
      ) : logs.length === 0 ? (
        <div className="card" style={{ padding: 48, textAlign: 'center', color: 'var(--color-ink-faint)' }}>
          <CheckCircle2 size={36} style={{ marginBottom: 12, opacity: 0.4 }} />
          <p style={{ margin: 0 }}>No sterilisation logs yet. Add your first log.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: groupBy === 'none' ? 10 : 16 }}>
          {Object.entries(groups).map(([groupKey, groupLogs]) => (
            <div key={groupKey}>
              {groupBy !== 'none' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, padding: '6px 12px', background: 'var(--color-surface)', borderRadius: 8, border: '1px solid var(--color-border)' }}>
                  <Layers size={13} color="var(--color-ink-faint)" />
                  <span style={{ fontWeight: 700, fontSize: 13 }}>{groupKey}</span>
                  <span style={{ fontSize: 12, color: 'var(--color-ink-faint)' }}>· {groupLogs.length} record{groupLogs.length > 1 ? 's' : ''}</span>
                </div>
              )}
              <div style={{ display: 'grid', gap: 10 }}>
                {groupLogs.map(log => {
                  const rs = RESULT_STYLE[log.result] || RESULT_STYLE.pass;
                  const ResultIcon = rs.icon;
                  return (
                    <div key={log._id} className="card" style={{ padding: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 700, fontSize: 14 }}>{log.equipment}</span>
                            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 100, background: rs.bg, color: rs.color, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
                              <ResultIcon size={11} />{rs.label}
                            </span>
                            {log.cycleNumber && <span style={{ fontSize: 11, color: 'var(--color-ink-faint)', fontFamily: 'var(--font-mono)' }}>#{log.cycleNumber}</span>}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--color-ink-faint)', display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                            <span>📅 {log.date}{log.time ? ' · ' + log.time : ''}</span>
                            {log.method && <span>🧪 {log.method}</span>}
                            {log.operator && <span>👤 {log.operator}</span>}
                            {log.itemsCount > 0 && <span>📦 {log.itemsCount} item{log.itemsCount > 1 ? 's' : ''}</span>}
                            {log.expiryDate && <span style={{ color: new Date(log.expiryDate) < new Date() ? '#dc2626' : 'var(--color-ink-faint)' }}>
                              Expiry: {log.expiryDate} {new Date(log.expiryDate) < new Date() ? '⚠️' : ''}
                            </span>}
                          </div>
                          {log.items && <div style={{ fontSize: 12, color: 'var(--color-ink-soft)', marginTop: 4, fontStyle: 'italic' }}>{log.items}</div>}
                          {log.notes && <div style={{ fontSize: 12, color: 'var(--color-ink-faint)', marginTop: 3 }}>Note: {log.notes}</div>}
                        </div>
                        {isDoctor && (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="btn btn-outline btn-sm" onClick={() => { setEditingId(log._id); setForm({ ...emptyForm, ...log }); setShowForm(true); }}><Pencil size={13} /></button>
                            <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(log)}><Trash2 size={13} /></button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete this log?"
        message={`Delete sterilisation record for "${deleteTarget?.equipment}" on ${deleteTarget?.date}?`}
        confirmLabel="Delete"
        danger
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </AppLayout>
  );
}
